import { assertNever } from '../lib/assert-never.js';
import {
  DEFAULT_FEEDBACK_THANKS,
  type ExperienceBlock,
  type ExperienceBlockDraft,
  type ExperiencePage,
} from '../schemas/experience.js';
import {
  type NoyauCode,
  type NoyauDeterminationState,
  isNoyauSkipped,
  noyauOf,
} from '../schemas/noyau.js';
import type { SessionValueProblem, SessionValues } from '../schemas/session-play.js';
import { type DisplayConditionSources, isBlockDisplayed } from './conditions.js';
import { pageDependsOnNoyau } from './noyau.js';
import type { ExperienceVariable } from './variables.js';

/**
 * How a SESSION walks an experience (refonte modulaire §5.3, §9.2, §9.5).
 *
 * Pure, and shared because both ends need the same answers: the server decides
 * what the next page is and refuses an advance, the browser draws the page it
 * is handed and knows whether the protocol page is a seam. Two spellings of
 * « quelle est la page suivante » would put the two on different pages the day
 * a protocol page is emptied.
 *
 * ## What is NOT here
 *
 * The greying of a button lives in the reader (`components/reader/reader-play.ts`,
 * `readerBlockers`) and stays there. §9.2 asks for the check TWICE - « au
 * navigateur pour la main de la personne, au serveur à l'avancée de page » - and
 * the two are not the same rule: the browser knows which temps have appeared
 * because it played them, the server infers it from the button that was pressed.
 * The half below is the server's, and the browser's own copy is what greys the
 * button before the press.
 */

/* ----------------------------------------------------------------- the pages */

/**
 * Whether a page is SERVED at all.
 *
 * A protocol page carrying no protocol is skipped (§9.5, « une page protocole
 * vide se saute »): there is nothing to converse with, and the reader draws no
 * block on it - a person would see an empty frame with no way out.
 *
 * And a page that DEPENDS on the noyau - it carries a Noyau block, or cites
 * `@noyau` (`pageDependsOnNoyau`) - is skipped the same way when the
 * determination is `no-extracts` or `failed` (noyau.md §4.5): the person gets
 * no noyau, and nothing is invented in its place - no pair drawn at random, no
 * choice among the six. `determination` is the state of the session's
 * determination, null when it has none (an experience without a Noyau block, a
 * protocol not closed yet); a `pending` one leaves the page playable, because
 * the wait is at the BUTTON that leads there (§4.6), never on the page.
 *
 * ONE page against ONE state. A session is walked by `playablePages` and its
 * neighbours below, which also know what a page alone cannot: whether the
 * experience conducts a conversation at all (`sequenceState`).
 */
export function isPlayablePage(
  page: ExperiencePage,
  determination: NoyauDeterminationState | null = null,
): boolean {
  if (page.kind === 'protocol') return page.protocol !== null;
  return !(isNoyauSkipped(determination) && pageDependsOnNoyau(page));
}

/**
 * The state the noyau sequence of these pages plays under: the determination's
 * own, unless the experience conducts NO conversation - its protocol page is
 * empty, or a draft has none.
 *
 * Such an experience never closes a protocol, so it never gets a determination,
 * and a null state would read « not settled yet » for ever - the pages of the
 * sequence would stay playable on a determination that can never come. noyau.md
 * says what it is instead: « la séquence se saute toujours, puisque rien n'aura
 * été recueilli » (§3.4, §11), which is `no-extracts`, read off the pages.
 */
function sequenceState(
  pages: readonly ExperiencePage[],
  determination: NoyauDeterminationState | null,
): NoyauDeterminationState | null {
  const converses = pages.some((page) => page.kind === 'protocol' && page.protocol !== null);
  return converses ? determination : 'no-extracts';
}

/**
 * The pages a session actually walks, in order - the sequence skipped when its
 * determination settled on nothing, and for good when no conversation is
 * conducted (`sequenceState`).
 */
export function playablePages(
  pages: readonly ExperiencePage[],
  determination: NoyauDeterminationState | null = null,
): ExperiencePage[] {
  const state = sequenceState(pages, determination);
  return pages.filter((page) => isPlayablePage(page, state));
}

/** The first page served, or null for an experience of empty protocol pages only. */
export function firstPlayablePage(
  pages: readonly ExperiencePage[],
  determination: NoyauDeterminationState | null = null,
): ExperiencePage | null {
  return playablePages(pages, determination)[0] ?? null;
}

/**
 * The page AFTER `pageId`, or null when there is none - which is what makes the
 * session `terminée` (§5.3).
 *
 * An unknown `pageId` answers null rather than the first page: the caller has
 * already refused the advance by then, and answering « the beginning » would
 * replay the experience from a stale tab.
 *
 * The pages that depend on the noyau are skipped when the sequence is - on a
 * `no-extracts` or `failed` determination (§4.5), and on an experience whose
 * protocol page is empty (§3.4) - so « la session continue à la page jouable
 * suivante », and with none left the existing rule holds as it is: the advance
 * ends the session on the page it is on.
 */
export function nextPlayablePage(
  pages: readonly ExperiencePage[],
  pageId: string,
  determination: NoyauDeterminationState | null = null,
): ExperiencePage | null {
  const playable = playablePages(pages, determination);
  const index = playable.findIndex((page) => page.id === pageId);
  if (index === -1) return null;
  return playable[index + 1] ?? null;
}

/** Whether `pageId` is the LAST page served - the end of §5.3. */
export function isLastPlayablePage(
  pages: readonly ExperiencePage[],
  pageId: string,
  determination: NoyauDeterminationState | null = null,
): boolean {
  const playable = playablePages(pages, determination);
  return playable.length > 0 && playable[playable.length - 1]?.id === pageId;
}

/**
 * The FEEDBACK page of an experience (retour-experience.md §2), or null when it
 * has none.
 *
 * ONE reading of « the page of the retour » for the server that admits the
 * single advance of a closed session towards it (§6.2) and the single submit
 * from it (§5), and for the reader that offers « Donner mon avis ». At most one
 * exists in a legal content; on an illegal one - which only a draft can be,
 * `feedback-repeated` - the FIRST is the one answered, the page a session would
 * play first. Not `isPlayablePage`-filtered: the page is reached by its kind,
 * and it depends on the noyau only if its own blocks say so.
 */
export function feedbackPageOf<Page extends { readonly kind: ExperiencePage['kind'] }>(
  pages: readonly Page[],
): Page | null {
  return pages.find((page) => page.kind === 'feedback') ?? null;
}

/**
 * What the feedback page says once the feedback is sent (§2, §5): its phrase of
 * thanks, or the template's when the content holds none.
 *
 * An ABSENT phrase reads as the default - a content stored before issue #176
 * carries none on any page - and a PRESENT one is read as it stands, even
 * empty: an empty phrase is the refusal `feedback-thanks-empty` at the save,
 * and a version that somehow holds one would show nothing rather than a phrase
 * nobody wrote.
 */
export function feedbackThanksOf(page: { readonly thanks?: string | undefined }): string {
  return page.thanks ?? DEFAULT_FEEDBACK_THANKS;
}

/** The page a session is on, inside a frozen content. */
export function pageOf(
  pages: readonly ExperiencePage[],
  pageId: string | null,
): ExperiencePage | null {
  if (pageId === null) return null;
  return pages.find((page) => page.id === pageId) ?? null;
}

/* ----------------------------------------------------------- what has appeared */

/** Every block of the page, in reading order, whatever its temps. */
export function pageBlocks(page: Pick<ExperiencePage, 'temps'>): ExperienceBlock[] {
  return page.temps.flatMap((temps) => temps.blocks);
}

/**
 * How many temps had appeared when the person pressed `pressedBlockId` (H30).
 *
 * The temps holding the button, plus every temps before it - a button cannot be
 * pressed before it appears, and nothing after it has played yet. A null button
 * is the automatic duration of §9.2 running out, and it answers the whole page:
 * the duration is counted from the page, so by the time it fires the page has
 * had every chance to finish appearing.
 *
 * It is an INFERENCE and it is deliberately generous in one direction: a temps
 * that started on a delay and had not yet appeared is counted as appeared when
 * a later button is pressed, which cannot happen - a button of a later temps has
 * not appeared either. It is never generous in the other direction, which is
 * the one that matters: a required field of a temps that has NOT played can
 * never refuse an advance.
 */
export function revealedTempsCount(page: ExperiencePage, pressedBlockId: string | null): number {
  if (pressedBlockId === null) return page.temps.length;
  const index = page.temps.findIndex((temps) =>
    temps.blocks.some((block) => block.id === pressedBlockId),
  );
  return index === -1 ? page.temps.length : index + 1;
}

/** The blocks of the temps that had appeared, in reading order. */
export function appearedPageBlocks(page: ExperiencePage, revealed: number): ExperienceBlock[] {
  return page.temps.slice(0, Math.max(0, revealed)).flatMap((temps) => temps.blocks);
}

/* ------------------------------------------------------------- the refusals */

/**
 * Deliberately permissive, and the SAME pattern the reader greys on: one `@`,
 * something on each side, a dot in the domain.
 *
 * A stricter server pattern than the browser's would be a page whose button is
 * armed and whose advance is refused - the one failure mode the double check of
 * §9.2 exists to avoid.
 */
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isSessionEmail(value: string): boolean {
  return EMAIL.test(value);
}

/** What the values hold for a block, as a string. A tick is not a text. */
export function sessionText(values: SessionValues, blockId: string): string {
  const value = values[blockId];
  return typeof value === 'string' ? value : '';
}

/** What the values hold for a block, as a tick. */
export function sessionTicked(values: SessionValues, blockId: string): boolean {
  return values[blockId] === true;
}

/**
 * What refuses the advance, among the blocks that had ALREADY APPEARED (§9.2,
 * H30) and that the page SHOWS (§9.2, H70).
 *
 * Counted from `revealed` and never from the page: a required field sitting in
 * a temps that has not played cannot refuse the press that would have played it.
 *
 * And judged on the DISPLAYED blocks only: « un bloc masqué n'existe pas pour la
 * page », so a required field whose display conditions do not hold refuses
 * nothing - at the server as at the browser, which greys its button on the very
 * same `isBlockDisplayed` (H72). `sources` is what the conditions read: the
 * session's own values, its retained noyau and the statuses of its objectives,
 * never what the advance posts - a condition cites a page BEFORE this one (H60),
 * whose values the session already holds, and the browser is not a trust
 * boundary.
 */
export function advanceProblems(
  page: ExperiencePage,
  revealed: number,
  values: SessionValues,
  sources: DisplayConditionSources,
): SessionValueProblem[] {
  const problems: SessionValueProblem[] = [];
  for (const block of appearedPageBlocks(page, revealed)) {
    if (!isBlockDisplayed(block, sources)) continue;
    if (block.type === 'checkbox') {
      if (block.required && !sessionTicked(values, block.id)) {
        problems.push({ blockId: block.id, kind: 'required' });
      }
      continue;
    }
    // A Sélection and a Note are refused like an Input: required, and holding
    // nothing (retour-experience.md §10). Their value is a text - the label of
    // the option, the digit of the stars - so the reading is the Input's.
    if (block.type === 'selection' || block.type === 'note') {
      if (block.required && sessionText(values, block.id).trim() === '') {
        problems.push({ blockId: block.id, kind: 'required' });
      }
      continue;
    }
    if (block.type !== 'input') continue;
    const text = sessionText(values, block.id).trim();
    if (block.required && text === '') {
      problems.push({ blockId: block.id, kind: 'required' });
      continue;
    }
    if (
      block.lines === 'single' &&
      block.format === 'email' &&
      text !== '' &&
      !isSessionEmail(text)
    ) {
      problems.push({ blockId: block.id, kind: 'email' });
    }
  }
  return problems;
}

/* -------------------------------------------------------------- the variables */

/**
 * What a block WRITES into the session, or null when it writes nothing (§8.6).
 *
 * The name is frozen beside the value, and it is the block's `variableName`
 * when the operator gave it one. When the operator gave none - a legal content,
 * §8.6 makes the name a mere label - the block's own LABEL is frozen instead,
 * trimmed to the length a name may have. Two reasons, and neither is
 * decoration: `sessionVariableSchema` wants a non-empty name (#50), and §5.3
 * has the session remember « les valeurs déjà saisies », all of them, so a
 * nameless required field must survive a reprise like every other.
 */
export const SESSION_VARIABLE_NAME_MAX = 40;

export function sessionVariableNameOf(block: ExperienceBlock): string | null {
  if (!isFieldBlock(block) && block.type !== 'button') return null;
  const named = block.variableName.trim();
  if (named !== '') return named.slice(0, SESSION_VARIABLE_NAME_MAX);
  if (block.type === 'button') return null;
  const label = (
    block.type === 'selection' || block.type === 'note' ? block.question : block.label
  ).trim();
  return label === '' ? null : label.slice(0, SESSION_VARIABLE_NAME_MAX);
}

/**
 * The four blocks that WRITE a value into the session under a `{{champ:<id>}}`
 * (§8.6): an Input, a Checkbox and - issue #176 - a Sélection and a Note.
 *
 * ONE list, for the declaration of the variables, their names, the resolution of
 * a token and the reading of Sessions: written four times, the day a fifth
 * block writes a field one of them would forget it. A Bouton writes the choice
 * of its PAGE and is not a field; a Lien and a Texte copiable write nothing.
 */
export type FieldBlock = Extract<
  ExperienceBlock,
  { type: 'input' | 'checkbox' | 'selection' | 'note' }
>;

export function isFieldBlock(block: ExperienceBlock): block is FieldBlock {
  return (
    block.type === 'input' ||
    block.type === 'checkbox' ||
    block.type === 'selection' ||
    block.type === 'note'
  );
}

/**
 * Whether this block NAMES THE PERSON in Sessions (§9.2 of the refonte, H75): an
 * Input on ONE line that ticks « Nom de la personne dans Sessions ».
 *
 * The one reading of the flag, for the refusal of a second holder
 * (`person-name-repeated`) and for the list of Sessions that shows its value
 * (#152). A multi-line Input that still carries the flag names nobody: §9.2
 * offers the box on a one-line Input only, and the inspector clears it when the
 * Input grows lines (#150) - so a stale flag is neither refused nor read.
 */
export function isPersonNameInput(block: ExperienceBlock | ExperienceBlockDraft): boolean {
  return block.type === 'input' && block.personName && block.lines === 'single';
}

/**
 * The Bouton whose label the page retains as its choice, or null (§8.6).
 *
 * « Un bouton seul n'écrit rien : il n'y a pas de choix quand il n'y a qu'une
 * issue. » So a page with fewer than two buttons never writes a choice, and a
 * button that did not tick « Retenir le choix » never writes one either.
 *
 * The NAME of the choice is the page's, not the button's: §8.6 has « une seule
 * variable par page », so the first button of the page that carries both the
 * flag and a name gives the variable its label - a person pressing the second
 * button writes the same variable as one pressing the first.
 */
export function pageChoiceName(page: ExperiencePage): string | null {
  const buttons = pageBlocks(page).filter((block) => block.type === 'button');
  if (buttons.length < 2) return null;
  for (const button of buttons) {
    if (button.type !== 'button' || !button.rememberChoice) continue;
    const name = sessionVariableNameOf(button);
    if (name !== null) return name;
  }
  return null;
}

/* ------------------------------------------------- what the PERSON reads */

/**
 * What a text shown to the PERSON has to resolve against (§8.6; grill Variables
 * of 2026-09-16, D4): the values of the session, the live matter of each
 * objective and the frozen direction of each criterion - the three the session
 * view of §5.2 carries. The last two are optional at the régime of the view.
 */
export type PersonVariableSources = {
  readonly values: SessionValues;
  readonly matter?: Readonly<Record<string, readonly string[]>> | undefined;
  readonly criterionDirections?: Readonly<Record<string, string>> | undefined;
  /** The RETAINED noyau (noyau.md §3.3), after the first choice; absent or null before, or when skipped. */
  readonly noyau?: NoyauCode | null | undefined;
};

/**
 * What `@objectif` renders on a PAGE (D4): the live extracts alone, in the
 * person's own words, one per line - never the objective's name, never its
 * status, which §9.5 forbids showing the person - and NOTHING when there is no
 * matter. At that point the protocol is over, so the interviewer's « rien
 * encore » (§8.6) would be false, and an empty list is a fact of the session,
 * not a defect of the experience.
 */
export function objectiveForPerson(extracts: readonly string[]): string {
  return extracts
    .map((extract) => extract.trim())
    .filter((extract) => extract !== '')
    .join('\n');
}

/**
 * What a variable renders as, on a page shown to the person (§8.6).
 *
 * TOTAL: it always answers a string, and the empty string when there is nothing
 * to show. The « jamais rendue vide en silence » of §8.6 protects the OPERATOR
 * from a defect, and the defect is caught where the operator is - the save
 * refuses an unresolved or out-of-reach variable (§9.1, D3). What reaches the
 * person is a legal experience in a state of its own: an optional field left
 * blank, an objective without matter. A raw `{{champ:...}}` on the person's
 * screen is the one thing that must never happen, whatever the cause.
 *
 * A tick reads « oui » / « non », because a sentence is being completed. The
 * retained noyau reads its DEPOSITED name, « Souverain Magicien », singular and
 * as it stands (N11): the operator turns the sentence around it.
 */
export function resolvePersonVariable(
  sources: PersonVariableSources,
  variable: ExperienceVariable,
): string {
  switch (variable.kind) {
    case 'field':
    case 'choice': {
      const value = sources.values[variable.id];
      if (value === undefined) return '';
      if (typeof value === 'boolean') return value ? 'oui' : 'non';
      return value;
    }
    case 'objective':
      return objectiveForPerson(sources.matter?.[variable.id] ?? []);
    case 'criterion':
      return sources.criterionDirections?.[variable.id] ?? '';
    case 'noyau':
      return sources.noyau == null ? '' : noyauOf(sources.noyau).name;
    default:
      return assertNever(variable.kind, 'person variable kind');
  }
}
